import axios from 'axios';
import MainURL from '../MainURL';

export type ContractTypeId = 'web' | 'app' | 'maintenance' | 'consulting';

export type OnlineContractStatus = 'pending' | 'signed';

export type PaymentTerm = {
  category: string;
  amount: string;
  condition: string;
};

export type OnlineContract = {
  id: number;
  customer_name: string;
  phone: string;
  contract_type: ContractTypeId | string;
  contract_name: string | null;
  product_name: string | null;
  company_name: string | null;
  representative: string | null;
  business_number: string | null;
  email: string | null;
  address: string | null;
  build_cost: string | null;
  build_period: string | null;
  maintenance_fee: string | null;
  maintenance_period: string | null;
  payment_terms: PaymentTerm[];
  special_notes: string | null;
  provider_company_name: string | null;
  provider_representative: string | null;
  provider_business_number: string | null;
  provider_contact: string | null;
  provider_address: string | null;
  provider_account_number: string | null;
  terms_text: string;
  status: OnlineContractStatus | string;
  terms_agreed?: boolean;
  privacy_agreed?: boolean;
  signature_name?: string | null;
  signature_image?: string | null;
  signed_at?: string | null;
  created_by_name?: string | null;
  created_by_email?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type CreateContractPayload = {
  customer_name: string;
  phone: string;
  contract_type: ContractTypeId | string;
  contract_name?: string;
  product_name?: string;
  company_name?: string;
  representative?: string;
  business_number?: string;
  email?: string;
  address?: string;
  build_cost?: string;
  build_period?: string;
  maintenance_fee?: string;
  maintenance_period?: string;
  payment_terms?: PaymentTerm[];
  special_notes?: string;
  terms_text?: string;
  created_by_name?: string;
  created_by_email?: string;
};

export type FetchContractListOptions = {
  q?: string;
  status?: 'ALL' | OnlineContractStatus | string;
};

type ApiErrorBody = {
  ok?: boolean;
  error?: { code?: string; message?: string };
  message?: string;
};

const ADMIN_API_BASE = `${MainURL}/api/admin/contracts`;

function getErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.error?.message) return body.error.message;
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

export async function fetchContractList(options?: FetchContractListOptions) {
  try {
    const params: Record<string, string> = {};
    if (options?.q) params.q = options.q;
    if (options?.status && options.status !== 'ALL') params.status = options.status;

    const res = await axios.get<{ ok: boolean; data?: OnlineContract[] }>(
      `${ADMIN_API_BASE}/list`,
      { params: Object.keys(params).length ? params : undefined },
    );
    if (!res.data?.ok || !Array.isArray(res.data.data)) {
      throw new Error('계약 목록 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '계약 목록을 불러오지 못했습니다.'));
  }
}

export async function fetchContractDetail(id: number) {
  try {
    const res = await axios.get<{ ok: boolean; data?: OnlineContract }>(
      `${ADMIN_API_BASE}/${id}`,
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('계약 상세 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '계약 상세를 불러오지 못했습니다.'));
  }
}

export async function createContract(payload: CreateContractPayload) {
  try {
    const res = await axios.post<{ ok: boolean; data?: OnlineContract }>(
      ADMIN_API_BASE,
      payload,
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('계약 등록 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '계약 등록에 실패했습니다.'));
  }
}

export async function updateContract(id: number, payload: CreateContractPayload) {
  try {
    const res = await axios.patch<{ ok: boolean; data?: OnlineContract }>(
      `${ADMIN_API_BASE}/${id}`,
      payload,
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('계약 수정 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '계약 수정에 실패했습니다.'));
  }
}

export async function deleteContract(id: number) {
  try {
    const res = await axios.delete<{ ok: boolean }>(`${ADMIN_API_BASE}/${id}`);
    if (!res.data?.ok) {
      throw new Error('계약 삭제 응답 형식이 올바르지 않습니다.');
    }
  } catch (err) {
    throw new Error(getErrorMessage(err, '계약 삭제에 실패했습니다.'));
  }
}

